// =============================================================================
// Session cache for the list pages' DEFAULT first-page search results
// (Quotes / Orders / Shipments).
//
// prefetchSearchResults() fires shortly after login (and on app boot when
// already signed in) and loads each page's mount query — blank filters,
// page 1, newest first — so landing on the page binds rows instantly from
// memory instead of waiting on the network. The pages then revalidate
// quietly in the background and write every fresh default-search response
// back here, so navigating away and back stays instant AND current.
//
// The prefetch is deliberately DELAYED a beat so the dashboard's own calls
// own the wire first — warming these lists must never slow the landing page.
//
// Same lifecycle as refDataCache: module-level (survives route changes, not a
// hard reload) and wiped by clearAuth() on logout / 401.
// =============================================================================
import sessionStorage from "./sessionStore";

const API_URL = process.env.REACT_APP_API_URL;

// Runway for the dashboard's own requests before the background search
// prefetch starts competing for connections / API workers.
const PREFETCH_DELAY_MS = 2500;

export const QUOTES_KEY = "quotes";
export const ORDERS_KEY = "orders";
export const SHIPMENTS_KEY = "shipments";

// key -> { data: object | null, promise: Promise | null }
const store = new Map();

// Synchronous read — the normalized results object, or undefined if absent.
export function peek(key) {
  const entry = store.get(key);
  return entry && entry.data ? entry.data : undefined;
}

// Store a fresh default-search result (called by the pages after every
// default-filter search, and by the prefetcher). An EMPTY result set is never
// stored: during API warm-up / Epicor congestion the default search can
// legitimately answer "success, 0 rows", and pinning that would make the page
// open on "no results" for the rest of the session while looking instant.
export function put(key, data) {
  if (!data) return;
  const rows = data.quotes || data.orders || data.shipments;
  if (!Array.isArray(rows) || rows.length === 0) return;
  store.set(key, { data, promise: null });
}

// Wipe everything — called from clearAuth() on logout / 401.
export function clear() {
  store.clear();
}

function authedFetchJson(url) {
  const headers = {};
  const token = sessionStorage.getItem("authToken");
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { headers }).then((r) =>
    r.ok ? r.json() : Promise.reject(r.status)
  );
}

// -----------------------------------------------------------------------------
// Normalizers — ONE result shape shared by each page's runSearch and the
// prefetcher, so a cached bind is indistinguishable from a live response.
// -----------------------------------------------------------------------------
export function normalizeQuotesResponse(data) {
  return {
    quotes: Array.isArray(data.quotes) ? data.quotes : [],
    totalCount: data.totalCount ?? null,
    totalPages: data.totalPages ?? 0,
    // Cursor path sends these; the legacy skip path leaves them undefined.
    hasMore: typeof data.hasMore === "boolean" ? data.hasMore : undefined,
    hasPrev: typeof data.hasPrev === "boolean" ? data.hasPrev : undefined,
    firstQuoteNum: data.firstQuoteNum ?? null,
    lastQuoteNum: data.lastQuoteNum ?? null,
    // Rep-scan only: the scan is row-capped, so results are incomplete and
    // the total is a floor.
    repScanTruncated: data.repScanTruncated === true,
    message: data.message ?? null,
  };
}

export function normalizeOrdersResponse(data) {
  return {
    orders: Array.isArray(data.orders) ? data.orders : [],
    totalCount: data.totalCount ?? null,
    totalPages: data.totalPages ?? 0,
    // Live (cache-free) mode: no total — pager runs on hasMore/hasPrev plus
    // boundary OrderNums. All undefined on the cached path.
    hasMore: typeof data.hasMore === "boolean" ? data.hasMore : undefined,
    hasPrev: typeof data.hasPrev === "boolean" ? data.hasPrev : undefined,
    firstOrderNum: data.firstOrderNum ?? null,
    lastOrderNum: data.lastOrderNum ?? null,
    // The server's "Partial results — scan limit reached…" note (UI-ORD-025): shown on
    // screen so an empty page explains itself instead of reading like a dead end.
    scanCapHit: data.scanCapHit === true,
    message: data.message || null,
  };
}

export function normalizeShipmentsResponse(data) {
  return {
    shipments: Array.isArray(data.shipments) ? data.shipments : [],
    totalCount: data.totalCount ?? null,
    totalPages: data.totalPages ?? 0,
    hasMore: typeof data.hasMore === "boolean" ? data.hasMore : undefined,
    hasPrev: typeof data.hasPrev === "boolean" ? data.hasPrev : undefined,
    firstPackNum: data.firstPackNum ?? null,
    lastPackNum: data.lastPackNum ?? null,
    // Issue 13: rows whose amount the API has not priced yet (it prices packs in
    // the background and answers inside a short budget). The page re-reads while
    // this is > 0 and shows "pricing…" instead of a false $0.00.
    amountsPending: typeof data.amountsPending === "number"
      ? data.amountsPending
      : (Array.isArray(data.shipments) ? data.shipments.filter((s) => s && s.amountPending).length : 0),
  };
}

// -----------------------------------------------------------------------------
// The three default mount queries — MUST mirror each page's runSearch with
// blank filters (page 1, pageSize 25, newest first), so the cache key the
// prefetcher fills is exactly what the page asks for on mount.
// `usable` rejects responses that aren't real data (the sales-order cache's
// "still warming" empty envelope) so we never pin an empty grid.
// -----------------------------------------------------------------------------
function defaultQs(extra) {
  const qs = new URLSearchParams();
  qs.set("page", "1");
  qs.set("pageSize", "25");
  Object.entries(extra).forEach(([k, v]) => qs.set(k, v));
  return qs.toString();
}

const PREFETCHES = [
  {
    key: QUOTES_KEY,
    url: () => `${API_URL}/api/Quote?${defaultQs({ orderby: "QuoteNum desc" })}`,
    normalize: normalizeQuotesResponse,
  },
  {
    key: ORDERS_KEY,
    url: () => `${API_URL}/api/sales-orders?${defaultQs({ sortBy: "OrderNum", sortDir: "desc" })}`,
    normalize: normalizeOrdersResponse,
    usable: (n, raw) =>
      !(raw?.cacheInfo?.live !== true && raw?.cacheInfo?.warm === false && n.orders.length === 0),
  },
  {
    key: SHIPMENTS_KEY,
    url: () => `${API_URL}/api/shipments?${defaultQs({ sortBy: "PackNum", sortDir: "desc" })}`,
    normalize: normalizeShipmentsResponse,
  },
];

function prefetchOne({ key, url, normalize, usable }) {
  const entry = store.get(key);
  // Already there (the user reached the page before the delay elapsed and its
  // own search filled the slot) or already in flight — nothing to do.
  if (entry && (entry.data || entry.promise)) return;

  const promise = authedFetchJson(url())
    .then((raw) => {
      if (!raw || raw.success === false) throw new Error("search failed");
      const data = normalize(raw);
      if (usable && !usable(data, raw)) {
        store.delete(key);
        return;
      }
      // Same emptiness rule as put(): never pin a 0-row default search
      // (likely an API warm-up artifact, not truth).
      const rows = data.quotes || data.orders || data.shipments;
      if (!Array.isArray(rows) || rows.length === 0) {
        store.delete(key);
        return;
      }
      // Don't clobber a fresher result a page stored while we were in flight.
      const cur = store.get(key);
      if (cur && cur.data) return;
      store.set(key, { data, promise: null });
    })
    .catch(() => {
      // Best-effort: drop the slot so the page's own mount search runs live.
      const cur = store.get(key);
      if (cur && cur.promise === promise) store.delete(key);
    });

  store.set(key, { data: null, promise });
}

// Called right after login and on app boot when already authenticated —
// alongside refDataCache.prefetchForSession(), but on a short delay so the
// dashboard's own calls are never queued behind these.
export function prefetchSearchResults() {
  if (!sessionStorage.getItem("authToken")) return;
  window.setTimeout(() => {
    if (!sessionStorage.getItem("authToken")) return; // logged out meanwhile
    PREFETCHES.forEach(prefetchOne);
  }, PREFETCH_DELAY_MS);
}
